import type { ThemeTokens } from "./derive"
import type { ThemeSeed } from "./seeds"

export const CUSTOM_THEME_STORAGE_KEY = "theme-customs"
const CUSTOM_THEME_STORAGE_VERSION = 1
export const MAX_CUSTOM_THEMES = 10

export type CustomThemeId = `custom:${string}`

export interface CustomThemeSeed extends ThemeSeed {
  id: CustomThemeId
  createdAt: string
  updatedAt: string
  tokens: ThemeTokens
}

type PersistedThemePayload = {
  version: number
  themes: CustomThemeSeed[]
}

function isColorSpec(value: unknown): boolean {
  if (!value || typeof value !== "object") return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.h === "number" &&
    Number.isFinite(candidate.h) &&
    typeof candidate.c === "number" &&
    Number.isFinite(candidate.c) &&
    typeof candidate.l === "number" &&
    Number.isFinite(candidate.l)
  )
}

function isCustomThemeSeed(value: unknown): value is CustomThemeSeed {
  if (!value || typeof value !== "object") return false
  const candidate = value as Record<string, unknown>
  if (!String(candidate.id || "").startsWith("custom:")) return false

  return (
    typeof candidate.id === "string" &&
    typeof candidate.label === "string" &&
    (candidate.mode === "dark" || candidate.mode === "light") &&
    typeof candidate.createdAt === "string" &&
    typeof candidate.updatedAt === "string" &&
    isColorSpec(candidate.base) &&
    isColorSpec(candidate.primary) &&
    isColorSpec(candidate.accent) &&
    candidate.tokens !== null &&
    typeof candidate.tokens === "object"
  )
}

function sanitizeThemes(input: unknown): CustomThemeSeed[] {
  if (!Array.isArray(input)) return []

  const seen = new Set<string>()
  const next: CustomThemeSeed[] = []

  for (const item of input) {
    if (!isCustomThemeSeed(item)) continue
    if (seen.has(item.id)) continue
    seen.add(item.id)
    next.push(item)
  }

  return next.slice(0, MAX_CUSTOM_THEMES)
}

export function getCustomThemesFromStorage(): CustomThemeSeed[] {
  if (typeof localStorage === "undefined") return []

  const raw = localStorage.getItem(CUSTOM_THEME_STORAGE_KEY)
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw) as PersistedThemePayload

    if (parsed && parsed.version === CUSTOM_THEME_STORAGE_VERSION) {
      const themes = sanitizeThemes(parsed.themes)
      if (themes.length !== parsed.themes.length) {
        writeCustomThemesToStorage(themes)
      }
      return themes
    }

    const legacyThemes = sanitizeThemes(parsed)
    if (legacyThemes.length > 0) {
      writeCustomThemesToStorage(legacyThemes)
    } else {
      localStorage.removeItem(CUSTOM_THEME_STORAGE_KEY)
    }
    return legacyThemes
  } catch {
    localStorage.removeItem(CUSTOM_THEME_STORAGE_KEY)
    return []
  }
}

export function writeCustomThemesToStorage(themes: CustomThemeSeed[]): void {
  if (typeof localStorage === "undefined") return

  const payload: PersistedThemePayload = {
    version: CUSTOM_THEME_STORAGE_VERSION,
    themes: sanitizeThemes(themes),
  }

  localStorage.setItem(CUSTOM_THEME_STORAGE_KEY, JSON.stringify(payload))
}

export function clampColorValue(
  value: number,
  min: number,
  max: number,
): number {
  if (!Number.isFinite(value)) return min
  if (value < min) return min
  if (value > max) return max
  return value
}

export function sanitizeCustomSeed(seed: ThemeSeed): ThemeSeed {
  return {
    ...seed,
    base: {
      h: clampColorValue(seed.base.h, 0, 360),
      c: clampColorValue(seed.base.c, 0, 0.4),
      l: clampColorValue(seed.base.l, 0, 1),
    },
    primary: {
      h: clampColorValue(seed.primary.h, 0, 360),
      c: clampColorValue(seed.primary.c, 0, 0.4),
      l: clampColorValue(seed.primary.l, 0, 1),
    },
    accent: {
      h: clampColorValue(seed.accent.h, 0, 360),
      c: clampColorValue(seed.accent.c, 0, 0.4),
      l: clampColorValue(seed.accent.l, 0, 1),
    },
  }
}

export function createCustomThemeId(label: string): CustomThemeId {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

  return `custom:${slug || crypto.randomUUID().slice(0, 8)}`
}
